
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Check, Star, ArrowRight } from "lucide-react";

const Pricing = () => {
  const navigate = useNavigate();
  const providerPlans = [
    {
      name: "Tier 1",
      description: "Perfect for single-location providers",
      price: "100",
      period: "per month",
      popular: false,
      features: [
        "1 location maximum",
        "Receive and manage referrals",
        "Basic analytics dashboard",
        "Email support",
        "Profile verification badge"
      ],
      limitations: []
    },
    {
      name: "Tier 2",
      description: "Ideal for small multi-location providers",
      price: "150",
      period: "per month",
      popular: true,
      features: [
        "2 locations maximum",
        "All Tier 1 features included",
        "Priority email support",
        "Enhanced analytics & reporting",
        "Custom facility branding"
      ],
      limitations: []
    },
    {
      name: "Tier 3",
      description: "For growing networks with unlimited expansion",
      price: "225",
      period: "per month",
      popular: false,
      features: [
        "3 locations included",
        "Unlimited additional locations (+$50/month each)",
        "All Tier 2 features included",
        "Premium placement in search results",
        "Dedicated account manager",
        "Custom integration support",
        "Advanced reporting dashboard"
      ],
      limitations: []
    }
  ];

  const placementProfessionalPlan = {
    name: "Placement Professionals",
    description: "For case managers, discharge planners, social workers, and housing stabilization service workers",
    price: "Free",
    period: "forever",
    features: [
      "Unlimited facility searches",
      "Advanced filtering by county and waiver type",
      "Secure messaging",
      "Real-time bed availability updates",
      "Mobile app access",
      "Referral tracking and management",
      "Client placement history",
      "24/7 support",
      "Direct provider communication"
    ]
  };

  return (
    <section id="pricing" className="py-24 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto">
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <Badge variant="secondary" className="text-sm font-medium">
            Pricing Plans
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
            Choose Your
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              {" "}Perfect Plan
            </span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Transparent pricing designed for Minnesota's care coordination network. 
            All plans include HIPAA-compliant communication and state regulatory compliance.
          </p>
        </div>

        {/* Placement Professional Plan */}
        <div className="mb-12">
          <Card className="max-w-lg mx-auto border-success/50 bg-gradient-to-br from-success/5 to-primary/5">
            <CardHeader className="text-center space-y-4">
              <div className="w-16 h-16 bg-gradient-to-br from-success to-primary rounded-2xl flex items-center justify-center mx-auto">
                <Star className="w-8 h-8 text-success-foreground" />
              </div>
              <div>
                <CardTitle className="text-2xl">{placementProfessionalPlan.name}</CardTitle>
                <CardDescription className="text-base">
                  {placementProfessionalPlan.description}
                </CardDescription>
              </div>
              <div className="space-y-1">
                <div className="text-4xl font-bold text-success">
                  {placementProfessionalPlan.price}
                </div>
                <div className="text-sm text-muted-foreground">
                  {placementProfessionalPlan.period}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {placementProfessionalPlan.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-success flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              <Button 
                className="w-full bg-gradient-to-r from-success to-primary hover:shadow-lg"
                onClick={() => navigate('/login')}
              >
                Sign Up Now
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Provider Plans */}
        <div className="space-y-8">
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-2">Provider Plans</h3>
            <p className="text-muted-foreground">
              Designed specifically for Minnesota care facilities and licensed providers
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {providerPlans.map((plan, index) => (
              <Card 
                key={index} 
                className={`relative group hover:shadow-lg transition-all duration-300 ${
                  plan.popular 
                    ? 'border-primary/50 bg-gradient-to-br from-primary/5 to-accent/5' 
                    : 'border-border/50'
                }`}
              >
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-primary to-accent">
                    Most Popular
                  </Badge>
                )}
                
                <CardHeader className="text-center space-y-4">
                  <div>
                    <CardTitle className="text-2xl">{plan.name}</CardTitle>
                    <CardDescription className="text-base">
                      {plan.description}
                    </CardDescription>
                  </div>
                  <div className="space-y-1">
                    <div className="text-4xl font-bold">
                      <span className="text-primary">${plan.price}</span>
                      <span className="text-lg font-normal text-muted-foreground">
                        /{plan.period.split(' ')[1]}
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {plan.period}
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-6">
                  <div className="space-y-3">
                    {plan.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-3">
                        <Check className="w-4 h-4 text-success flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Button 
                    className={`w-full ${
                      plan.popular 
                        ? 'bg-gradient-to-r from-primary to-primary-glow hover:shadow-lg' 
                        : ''
                    }`}
                    variant={plan.popular ? 'default' : 'outline'}
                    onClick={() => navigate('/login')}
                  >
                    {plan.price === "0" ? "Sign Up Now" : "Sign Up Now"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16 bg-gradient-to-r from-primary/5 to-accent/5 rounded-2xl p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-4">
              Frequently Asked Questions
            </h3>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Is there a setup fee to join?</h4>
                <p className="text-sm text-muted-foreground">
                  No additional setup fees for providers. Each plan includes full access to Minnesota's centralized placement network. Placement professionals, including case managers, social workers, and discharge planners, can search listings and submit referrals completely free.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Can I upload documents with my referral?</h4>
                <p className="text-sm text-muted-foreground">
                  CareKonnect uses an email-only referral system. When you submit a referral, your email client opens with pre-filled provider contact information. You can then attach documents like care plans or assessments directly to your email before sending.
                </p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Will I get notified when a provider responds?</h4>
                <p className="text-sm text-muted-foreground">
                  Yes. You'll receive email notifications when a referral is submitted, reviewed, accepted, declined, or marked as placed; so everyone stays in the loop.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Are you HIPAA compliant?</h4>
                <p className="text-sm text-muted-foreground">
                  CareKonnect uses an email-only referral system that does not store PHI/PII data on our platform. All referral communications happen through your secure email client, maintaining compliance with your organization's existing privacy requirements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
